import { useMemo, useState } from 'react';
import { FilterCard, MetricCard, MetricGrid, SectionCard } from '@/components/DataList';
import { ListTable, ScreenFrame } from '@/components/Screen';
import { Icon } from '@/components/icons';
import { admin } from '@/api/endpoints';
import { errorMessage, pickList } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Checkbox,
  Empty,
  ErrorBox,
  FlashMessage,
  Form,
  Input,
  Loading,
  ProSearch,
  Select,
  StatusBadge,
} from '@/components/ui';
import { formatDateTime, required, str } from '@/utils/format';

const ROLE_LABELS: Record<string, string> = {
  staff: 'موظف',
  support: 'دعم فني',
  admin: 'مدير',
  distributor: 'موزع',
};

const STAFF_PERM_OPTIONS: Array<{ key: string; label: string }> = [
  { key: 'users', label: 'المستخدمون' },
  { key: 'deposits', label: 'شحن المحافظ' },
  { key: 'recharge_review', label: 'مراجعة الشحن' },
  { key: 'products', label: 'المنتجات' },
  { key: 'finance', label: 'المالية' },
  { key: 'executor', label: 'منفذ USSD' },
  { key: 'settings', label: 'الإعدادات' },
  { key: 'support', label: 'الدعم' },
  { key: 'site', label: 'الموقع' },
  { key: 'partner_api', label: 'Partner API' },
];

const STAFF_ROLES = new Set(['staff', 'support', 'admin']);

export function StaffScreen() {
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [role, setRole] = useState('staff');
  const [perms, setPerms] = useState<Record<string, boolean>>({ support: true });
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const state = useAsync(
    () => admin.users({}),
    (p) => pickList(p, 'data', 'users', 'items'),
  );

  const staffList = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (state.data ?? []).filter((u) => {
      const r = str(u.role);
      if (!STAFF_ROLES.has(r) && r !== 'distributor') return false;
      if (roleFilter !== 'all' && r !== roleFilter) return false;
      if (!needle) return true;
      const hay = [str(u.name), str(u.phone), str(u.email), ROLE_LABELS[r] ?? r].join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [state.data, q, roleFilter]);

  const activeCount = staffList.filter((u) => str(u.status) === 'active').length;

  async function create() {
    const miss = required({ name, phone, password }, ['name', 'phone', 'password']);
    if (miss) {
      setMsg(miss);
      return;
    }
    const cleanPhone = phone.replace(/\D/g, '');
    if (!/^09\d{8}$/.test(cleanPhone)) {
      setMsg('رقم الهاتف غير صالح. استخدم 09xxxxxxxx.');
      return;
    }
    if (password.length < 10) {
      setMsg('كلمة المرور يجب أن تكون 10 أحرف على الأقل.');
      return;
    }
    if (password !== confirm) {
      setMsg('تأكيد كلمة المرور غير مطابق.');
      return;
    }
    const permissions = Object.fromEntries(
      Object.entries(perms).filter(([, enabled]) => enabled),
    );
    setBusy(true);
    const res = await admin.createStaff({
      name,
      phone: cleanPhone,
      password,
      role,
      permissions,
      staff_permissions_json: permissions,
    });
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تم إنشاء الحساب.') : errorMessage(res));
    if (res.success) {
      setName('');
      setPhone('');
      setPassword('');
      setConfirm('');
      state.reload();
    }
  }

  async function toggleStatus(id: unknown, status: string) {
    const next = status === 'active' ? 'blocked' : 'active';
    const res = await admin.userStatus({ user_id: id, status: next });
    if (!res.success) {
      window.alert(errorMessage(res));
      return;
    }
    state.reload();
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="users"
      title="الموظفون"
      subtitle="أنشئ حسابات الدعم والموظفين بصلاحيات محددة، وتابع حالتهم من القائمة."
      actions={
        <Button type="button" variant="secondary" onClick={() => state.reload()}>
          <Icon name="refresh" size={18} /> تحديث
        </Button>
      }
    >
      <MetricGrid>
        <MetricCard title="الحسابات" value={String(staffList.length)} icon={<Icon name="users" size={21} />} />
        <MetricCard title="فعالون" value={String(activeCount)} icon={<Icon name="check" size={21} />} />
      </MetricGrid>

      <SectionCard title="إنشاء حساب موظف" icon={<Icon name="users" size={24} />}>
        <Form onSubmit={() => void create()}>
          <div className="form-grid two-columns">
            <Input label="الاسم" value={name} onChange={(e) => setName(e.target.value)} required />
            <Input
              label="الهاتف"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="09xxxxxxxx"
              required
            />
            <Input
              label="كلمة المرور"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Input
              label="تأكيد كلمة المرور"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
            <Select
              label="الدور"
              value={role}
              onChange={setRole}
              options={[
                { value: 'staff', label: 'موظف' },
                { value: 'support', label: 'دعم فني' },
                { value: 'distributor', label: 'موزع' },
                { value: 'admin', label: 'مدير' },
              ]}
            />
          </div>

          {role === 'staff' || role === 'support' ? (
            <div className="staff-perms top-space">
              <strong>صلاحيات الموظف</strong>
              <div className="staff-perms-grid">
                {STAFF_PERM_OPTIONS.map((opt) => (
                  <Checkbox
                    key={opt.key}
                    label={opt.label}
                    checked={Boolean(perms[opt.key])}
                    onChange={(checked) =>
                      setPerms((prev) => ({ ...prev, [opt.key]: checked }))
                    }
                  />
                ))}
              </div>
            </div>
          ) : null}

          <FlashMessage message={msg} />
          <Button type="submit" disabled={busy}>
            {busy ? 'جاري الإنشاء...' : 'إنشاء موظف'}
          </Button>
        </Form>
      </SectionCard>

      <FilterCard title="تصفية الموظفين" icon={<Icon name="settings" size={24} />}>
        <ProSearch value={q} onChange={setQ} placeholder="بحث بالاسم أو الهاتف" />
        <Select
          label="الدور"
          value={roleFilter}
          onChange={setRoleFilter}
          options={[
            { value: 'all', label: 'الكل' },
            { value: 'staff', label: 'موظف' },
            { value: 'support', label: 'دعم فني' },
            { value: 'admin', label: 'مدير' },
            { value: 'distributor', label: 'موزع' },
          ]}
        />
      </FilterCard>

      <SectionCard title={`الحسابات (${staffList.length})`} icon={<Icon name="chart" size={24} />}>
        {staffList.length === 0 ? (
          <Empty title="لا حسابات مطابقة" hint="أنشئ موظفاً أو عدّل التصفية." />
        ) : (
          <ListTable headers={['الاسم', 'الهاتف', 'الدور', 'الحالة', 'تاريخ الإنشاء', 'إجراءات']}>
            <tbody>
              {staffList.map((u) => (
                <tr key={str(u.id)}>
                  <td>
                    <strong>{str(u.name)}</strong>
                  </td>
                  <td>
                    <span dir="ltr" lang="en">
                      {str(u.phone)}
                    </span>
                  </td>
                  <td>{ROLE_LABELS[str(u.role)] ?? str(u.role)}</td>
                  <td>
                    <StatusBadge status={u.status} />
                  </td>
                  <td>{formatDateTime(u.created_at)}</td>
                  <td>
                    <Button
                      type="button"
                      variant={str(u.status) === 'active' ? 'danger' : 'secondary'}
                      className="compact"
                      onClick={() => void toggleStatus(u.id, str(u.status))}
                    >
                      {str(u.status) === 'active' ? 'تعطيل' : 'تفعيل'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </ListTable>
        )}
      </SectionCard>
    </ScreenFrame>
  );
}
